import { formService } from './formService';
import type {
  FormGuestEntry,
  GuestEntryListResponse,
  CreateGuestEntryRequest,
  UpdateGuestEntryRequest,
} from '@/types/guest';

/**
 * Service for relational guest entry CRUD operations (Story 6-1).
 * Handles 409 BOOKING_FULL responses with structured error codes.
 */
class GuestEntryService {
  async listEntries(publicId: string, signal?: AbortSignal): Promise<GuestEntryListResponse> {
    return formService.getGuestEntries(publicId, signal);
  }

  async createEntry(publicId: string, request: CreateGuestEntryRequest): Promise<FormGuestEntry> {
    try {
      return await formService.createGuestEntry(publicId, request);
    } catch (error: unknown) {
      if (this.isBookingFull(error)) {
        throw new BookingFullError();
      }
      throw error;
    }
  }

  async updateEntry(publicId: string, entryId: string, request: UpdateGuestEntryRequest): Promise<FormGuestEntry> {
    return formService.updateGuestEntry(publicId, entryId, request);
  }

  async deleteEntry(publicId: string, entryId: string): Promise<void> {
    try {
      return await formService.deleteGuestEntry(publicId, entryId);
    } catch (error: unknown) {
      if (this.isNotFound(error)) {
        throw new EntryAlreadyRemovedError();
      }
      throw error;
    }
  }

  async getGuestCount(publicId: string): Promise<{ currentCount: number; declaredLimit: number; spotsRemaining: number }> {
    return formService.getGuestCount(publicId);
  }

  async getCount(publicId: string): Promise<{ currentCount: number; declaredLimit: number; spotsRemaining: number }> {
    return this.getGuestCount(publicId);
  }

  async designateMainGuest(publicId: string, entryId: string): Promise<FormGuestEntry> {
    try {
      return await formService.designateMainGuest(publicId, entryId);
    } catch (error: unknown) {
      if (this.isMainGuestConflict(error)) {
        const mainGuestName = this.extractMainGuestName(error);
        throw new MainGuestAlreadyDesignatedError(mainGuestName);
      }
      throw error;
    }
  }

  /**
   * Error shape after API client transformation:
   * - Spring ResponseStatusException: { status: 409, error: "Conflict", message: "BOOKING_FULL", ... }
   * - Custom exception handler: { error: "MAIN_GUEST_ALREADY_DESIGNATED", message: "...", mainGuestName: "..." }
   */
  private isBookingFull(error: unknown): boolean {
    if (typeof error === 'object' && error !== null) {
      const err = error as Record<string, unknown>;
      const is409 = err.code === 409 || err.status === 409;
      const errorMsg = String(err.error ?? err.message ?? '');
      return is409 && errorMsg.includes('BOOKING_FULL');
    }
    return false;
  }

  /**
   * Checks only `error` field (not HTTP status) because the custom
   * MainGuestConflictExceptionHandler returns { error, message, mainGuestName }
   * without a `status` field — unlike Spring's default error format used by isBookingFull.
   */
  private isMainGuestConflict(error: unknown): boolean {
    if (typeof error === 'object' && error !== null) {
      const err = error as Record<string, unknown>;
      return err.error === 'MAIN_GUEST_ALREADY_DESIGNATED';
    }
    return false;
  }

  private isNotFound(error: unknown): boolean {
    if (typeof error === 'object' && error !== null) {
      const err = error as Record<string, unknown>;
      return err.code === 404 || err.status === 404;
    }
    return false;
  }

  private extractMainGuestName(error: unknown): string {
    if (typeof error === 'object' && error !== null) {
      const err = error as Record<string, unknown>;
      return typeof err.mainGuestName === 'string' ? err.mainGuestName : 'Unknown';
    }
    return 'Unknown';
  }
}

export class BookingFullError extends Error {
  constructor() {
    super('BOOKING_FULL');
    this.name = 'BookingFullError';
  }
}

export class MainGuestAlreadyDesignatedError extends Error {
  public readonly mainGuestName: string;
  constructor(mainGuestName: string) {
    super('MAIN_GUEST_ALREADY_DESIGNATED');
    this.name = 'MainGuestAlreadyDesignatedError';
    this.mainGuestName = mainGuestName;
  }
}

export class EntryAlreadyRemovedError extends Error {
  constructor() {
    super('ENTRY_ALREADY_REMOVED');
    this.name = 'EntryAlreadyRemovedError';
  }
}

export const guestEntryService = new GuestEntryService();
